import type { EdgeSide } from '../engine';
import { compareSortOrders } from './ids';
import type { ChannelId, EntityBundle, FeedPage, MessageId, SortOrder, SyncCursor, WireMessage } from './types';

/** Describes one contiguous cached coverage range for an account conversation. */
export interface FeedSegment {
  readonly id: string;
  readonly accountId: string;
  readonly conversationId: ChannelId;
  readonly oldestSortOrder: SortOrder;
  readonly newestSortOrder: SortOrder;
  readonly reachesStart: boolean;
  readonly reachesLiveTail: boolean;
  readonly olderCursor?: SortOrder;
  readonly newerCursor?: SortOrder;
  readonly stale?: boolean;
}

/** Identifies how a fetched page attaches to existing coverage; before/after use the boundary sort order. */
export interface FeedPageMergeRequest {
  readonly kind: 'latest' | 'before' | 'after' | 'around';
  readonly boundarySortOrder?: SortOrder;
}

/** Result of a local feed read: `hit` has rows, `end` has a known edge, and `miss` names the fetch reason. */
export type LocalPageResult =
  | { readonly kind: 'hit'; readonly page: FeedPage }
  | { readonly kind: 'end'; readonly side: EdgeSide }
  | { readonly kind: 'miss'; readonly reason: 'no-segment' | 'segment-boundary' | 'stale' };

/**
 * Total order over sync cursors; later cursors compare greater.
 *
 * Production cursors are journal-entry ULIDs plus two sentinels that predate every
 * entry: '' (no cursor known) and '0' (empty journal). All of them order correctly
 * under plain string comparison, matching the server's `sort_order > ?` semantics.
 * Any non-ULID cursor source (the session test server) must emit strings that sort
 * the same way, e.g. zero-padded counters.
 */
export function compareSyncCursors(left: SyncCursor, right: SyncCursor): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

/** Returns the later of two sync cursors, keeping the stored side on ties. */
export function maxSyncCursor(stored: SyncCursor, incoming: SyncCursor): SyncCursor {
  return compareSyncCursors(stored, incoming) >= 0 ? stored : incoming;
}

/** Returns the standard miss used when no segment covers the requested range. */
export function noSegmentMiss(): Extract<LocalPageResult, { readonly kind: 'miss' }> {
  return { kind: 'miss', reason: 'no-segment' };
}

/** Returns the segment containing a sort order, or `undefined` when coverage is absent. */
export function findContainingSegment(
  segments: readonly FeedSegment[],
  sortOrder: SortOrder
): FeedSegment | undefined {
  return segments.find((segment) => containsSortOrder(segment, sortOrder));
}

/** Returns the live-tail segment, or `undefined` when cached coverage has no live tail. */
export function findLiveTailSegment(segments: readonly FeedSegment[]): FeedSegment | undefined {
  return segments.find((segment) => segment.reachesLiveTail);
}

/** Converts a before-read over a segment into a hit, known top edge, or typed miss. */
export function decideBefore(
  segment: FeedSegment | undefined,
  messages: readonly WireMessage[],
  entities: EntityBundle,
  syncCursor: SyncCursor
): LocalPageResult {
  if (!segment) return noSegmentMiss();
  if (segment.stale) return { kind: 'miss', reason: 'stale' };

  const first = messages[0];
  if (first) {
    return hitPage(messages, entities, syncCursor, { olderCursor: first.sortOrder });
  }

  return segment.reachesStart
    ? { kind: 'end', side: 'top' }
    : { kind: 'miss', reason: 'segment-boundary' };
}

/** Converts an after-read over a segment into a hit, known bottom edge, or typed miss. */
export function decideAfter(
  segment: FeedSegment | undefined,
  messages: readonly WireMessage[],
  entities: EntityBundle,
  syncCursor: SyncCursor
): LocalPageResult {
  if (!segment) return noSegmentMiss();
  if (segment.stale) return { kind: 'miss', reason: 'stale' };

  const last = messages.at(-1);
  if (last) {
    return hitPage(messages, entities, syncCursor, { newerCursor: last.sortOrder });
  }

  return segment.reachesLiveTail
    ? { kind: 'end', side: 'bottom' }
    : { kind: 'miss', reason: 'segment-boundary' };
}

/** Converts an around-read over a segment into a centered hit or typed miss. */
export function decideAround(
  segment: FeedSegment | undefined,
  messages: readonly WireMessage[],
  entities: EntityBundle,
  syncCursor: SyncCursor,
  anchor: { readonly id: MessageId; readonly sortOrder: SortOrder }
): LocalPageResult {
  if (!segment) return noSegmentMiss();
  if (segment.stale) return { kind: 'miss', reason: 'stale' };

  const first = messages[0];
  const last = messages.at(-1);
  if (!first || !last) return { kind: 'miss', reason: 'segment-boundary' };

  const reachedStart = segment.reachesStart &&
    compareSortOrders(first.sortOrder, segment.oldestSortOrder) === 0;
  const reachedLiveTail = segment.reachesLiveTail &&
    compareSortOrders(last.sortOrder, segment.newestSortOrder) === 0;
  const resolvedId = messages.find((message) => message.id === anchor.id)?.id ??
    messages.find((message) => compareSortOrders(message.sortOrder, anchor.sortOrder) === 0)?.id;
  return hitPage(messages, entities, syncCursor, {
    ...(reachedStart ? {} : { olderCursor: first.sortOrder }),
    ...(reachedLiveTail ? {} : { newerCursor: last.sortOrder }),
    ...(resolvedId === undefined ? {} : { resolvedId })
  });
}

/** Converts a latest-read over live-tail coverage into a hit or typed miss. */
export function decideLatest(
  segments: readonly FeedSegment[],
  messages: readonly WireMessage[],
  entities: EntityBundle,
  syncCursor: SyncCursor
): LocalPageResult {
  const segment = findLiveTailSegment(segments);
  if (!segment) {
    return segments.length === 0
      ? { kind: 'miss', reason: 'no-segment' }
      : { kind: 'miss', reason: 'segment-boundary' };
  }
  if (segment.stale) return { kind: 'miss', reason: 'stale' };

  const first = messages[0];
  const olderCursor = first && (compareSortOrders(first.sortOrder, segment.oldestSortOrder) > 0 || !segment.reachesStart)
    ? first.sortOrder
    : undefined;
  return hitPage(messages, entities, syncCursor, { olderCursor });
}

/** Builds a coverage segment from a nonempty page; returns `undefined` for empty pages. */
export function segmentFromPage(
  accountId: string,
  conversationId: ChannelId,
  page: FeedPage
): FeedSegment | undefined {
  const oldest = page.messages[0]?.sortOrder;
  const newest = page.messages.at(-1)?.sortOrder;
  if (!oldest || !newest) return undefined;

  const reachesStart = !page.olderCursor;
  const reachesLiveTail = !page.newerCursor;
  return {
    id: segmentId(accountId, conversationId, oldest, newest),
    accountId,
    conversationId,
    oldestSortOrder: oldest,
    newestSortOrder: newest,
    reachesStart,
    reachesLiveTail,
    ...(reachesStart ? {} : { olderCursor: page.olderCursor ?? oldest }),
    ...(reachesLiveTail ? {} : { newerCursor: page.newerCursor ?? newest })
  };
}

/** Merges a fetched page segment into existing coverage, preserving at most one live-tail segment. */
export function mergeSegments(
  existingSegments: readonly FeedSegment[],
  request: FeedPageMergeRequest,
  pageSegment: FeedSegment
): FeedSegment[] {
  const boundarySegmentId = boundaryConnectedSegmentId(existingSegments, request);
  const segments = pageSegment.reachesLiveTail
    ? existingSegments.map(clearLiveTail)
    : [...existingSegments];

  let merged = pageSegment;
  let mergedExisting = false;
  const remaining: FeedSegment[] = [];

  for (const segment of segments) {
    if (rangesOverlap(segment, merged) || segment.id === boundarySegmentId) {
      if (mergedExisting) {
        merged = mergeTwoSegments(merged, segment);
      } else {
        merged = mergeTwoSegments(segment, merged);
        mergedExisting = true;
      }
    } else {
      remaining.push(segment);
    }
  }

  return coalesceOverlappingSegments([...remaining, merged]);
}

/** Splits or shrinks a segment after rows are pruned, keeping only proven surviving runs. */
export function resegmentAfterPrune(
  segment: FeedSegment,
  survivingSortOrders: readonly SortOrder[],
  removedSortOrders: readonly SortOrder[]
): FeedSegment[] {
  const survivors = sortedUniqueSortOrders(survivingSortOrders.filter((sortOrder) =>
    containsSortOrder(segment, sortOrder)
  ));
  const firstSurvivor = survivors[0];
  if (!firstSurvivor) return [];

  const removed = sortedUniqueSortOrders(removedSortOrders.filter((sortOrder) =>
    containsSortOrder(segment, sortOrder)
  ));
  const runs: SurvivingRun[] = [];
  let runOldest = firstSurvivor;
  let runNewest = firstSurvivor;

  for (const sortOrder of survivors.slice(1)) {
    if (hasRemovedSortOrderBetween(removed, runNewest, sortOrder)) {
      runs.push({ oldestSortOrder: runOldest, newestSortOrder: runNewest });
      runOldest = sortOrder;
    }
    runNewest = sortOrder;
  }
  runs.push({ oldestSortOrder: runOldest, newestSortOrder: runNewest });

  return runs.map((run) => segmentFromSurvivingRun(segment, run));
}

/** Returns sorted coverage with overlapping ranges merged in order. */
export function coalesceOverlappingSegments(segments: readonly FeedSegment[]): FeedSegment[] {
  const sorted = [...segments].sort((left, right) => compareSortOrders(left.oldestSortOrder, right.oldestSortOrder));
  const coalesced: FeedSegment[] = [];

  for (const segment of sorted) {
    const previous = coalesced.at(-1);
    if (previous && rangesOverlap(previous, segment)) {
      coalesced[coalesced.length - 1] = mergeTwoSegments(previous, segment);
    } else {
      coalesced.push(segment);
    }
  }

  return coalesced;
}

/** Combines overlapping segments, keeping base identity and edge reachability from the outer bounds. */
export function mergeTwoSegments(base: FeedSegment, next: FeedSegment): FeedSegment {
  const oldestSource = compareSortOrders(base.oldestSortOrder, next.oldestSortOrder) <= 0 ? base : next;
  const newestSource = compareSortOrders(base.newestSortOrder, next.newestSortOrder) >= 0 ? base : next;
  const reachesStart = oldestSource.reachesStart;
  const reachesLiveTail = newestSource.reachesLiveTail;

  return {
    id: base.id,
    accountId: base.accountId,
    conversationId: base.conversationId,
    oldestSortOrder: oldestSource.oldestSortOrder,
    newestSortOrder: newestSource.newestSortOrder,
    reachesStart,
    reachesLiveTail,
    ...(reachesStart ? {} : { olderCursor: oldestSource.olderCursor ?? oldestSource.oldestSortOrder }),
    ...(reachesLiveTail ? {} : { newerCursor: newestSource.newerCursor ?? newestSource.newestSortOrder }),
    ...(base.stale && next.stale ? { stale: true } : {})
  };
}

/** Returns whether a sort order falls inclusively inside a segment. */
export function containsSortOrder(segment: FeedSegment, sortOrder: SortOrder): boolean {
  return compareSortOrders(segment.oldestSortOrder, sortOrder) <= 0
    && compareSortOrders(sortOrder, segment.newestSortOrder) <= 0;
}

/** Returns whether two segment ranges overlap inclusively. */
export function rangesOverlap(left: FeedSegment, right: FeedSegment): boolean {
  return compareSortOrders(left.oldestSortOrder, right.newestSortOrder) <= 0
    && compareSortOrders(right.oldestSortOrder, left.newestSortOrder) <= 0;
}

/** Returns a copy of a segment marked stale so reads produce typed stale misses. */
export function markSegmentStale(segment: FeedSegment): FeedSegment {
  return { ...segment, stale: true };
}

/** Builds the stable segment primary key from account, conversation, and range bounds. */
export function segmentId(accountId: string, conversationId: ChannelId, oldest: SortOrder, newest: SortOrder): string {
  return `${accountId}:${conversationId}:${oldest}:${newest}`;
}

function hitPage(
  messages: readonly WireMessage[],
  entities: EntityBundle,
  syncCursor: SyncCursor,
  cursors: {
    readonly newerCursor?: SortOrder;
    readonly olderCursor?: SortOrder;
    readonly resolvedId?: MessageId;
  }
): Extract<LocalPageResult, { readonly kind: 'hit' }> {
  return {
    kind: 'hit',
    page: {
      messages: [...messages],
      entities,
      ...(cursors.olderCursor === undefined ? {} : { olderCursor: cursors.olderCursor }),
      ...(cursors.newerCursor === undefined ? {} : { newerCursor: cursors.newerCursor }),
      ...(cursors.resolvedId === undefined ? {} : { resolvedId: cursors.resolvedId }),
      syncCursor
    }
  };
}

function boundaryConnectedSegmentId(
  segments: readonly FeedSegment[],
  request: FeedPageMergeRequest
): string | undefined {
  if (request.kind !== 'before' && request.kind !== 'after') return undefined;
  const boundarySortOrder = request.boundarySortOrder;
  if (!boundarySortOrder) return undefined;

  return findContainingSegment(segments, boundarySortOrder)?.id;
}

function clearLiveTail(segment: FeedSegment): FeedSegment {
  if (!segment.reachesLiveTail) return segment;
  return {
    ...segment,
    reachesLiveTail: false,
    newerCursor: segment.newerCursor ?? segment.newestSortOrder
  };
}

interface SurvivingRun {
  readonly oldestSortOrder: SortOrder;
  readonly newestSortOrder: SortOrder;
}

function segmentFromSurvivingRun(segment: FeedSegment, run: SurvivingRun): FeedSegment {
  const reachesStart =
    segment.reachesStart && compareSortOrders(run.oldestSortOrder, segment.oldestSortOrder) === 0;
  const reachesLiveTail =
    segment.reachesLiveTail && compareSortOrders(run.newestSortOrder, segment.newestSortOrder) === 0;

  return {
    id: segmentId(segment.accountId, segment.conversationId, run.oldestSortOrder, run.newestSortOrder),
    accountId: segment.accountId,
    conversationId: segment.conversationId,
    oldestSortOrder: run.oldestSortOrder,
    newestSortOrder: run.newestSortOrder,
    reachesStart,
    reachesLiveTail,
    ...(reachesStart ? {} : { olderCursor: run.oldestSortOrder }),
    ...(reachesLiveTail ? {} : { newerCursor: run.newestSortOrder }),
    ...(segment.stale ? { stale: true } : {})
  };
}

function hasRemovedSortOrderBetween(
  removedSortOrders: readonly SortOrder[],
  left: SortOrder,
  right: SortOrder
): boolean {
  return removedSortOrders.some((sortOrder) =>
    compareSortOrders(left, sortOrder) < 0 && compareSortOrders(sortOrder, right) < 0
  );
}

function sortedUniqueSortOrders(sortOrders: readonly SortOrder[]): SortOrder[] {
  const sorted = [...sortOrders].sort(compareSortOrders);
  const unique: SortOrder[] = [];

  for (const sortOrder of sorted) {
    const previous = unique.at(-1);
    if (!previous || compareSortOrders(previous, sortOrder) !== 0) {
      unique.push(sortOrder);
    }
  }

  return unique;
}
